/**
 * Envío AP-3 · «Reemplazar» un clip del timeline.
 *
 * Elegís otro contenido de la Galería o lo subís desde la computadora (con el
 * tope del plan a la vista) y el clip conserva su lugar, fundidos, volumen,
 * transform y transiciones. Duración: mantener la del clip o usar la del nuevo.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowRightLeft, Film, Info, Loader2, Music, Upload, X } from 'lucide-react';
import { toast } from 'sonner';
import useTimelineStore from '../../store/timelineStore';
import { galleryService } from '../../services/galleryService';
import { uploadToR2 } from '../../utils/uploadToR2';
import { describeUploadError, kindOf, limitMB } from '../../utils/uploadLimits';
import { useUploadLimits } from '../../hooks/useUploadLimits';
import { fmtSecs, fmtTime } from './timelineBarModel';
import '../../styles/timeline-bar.css';

const ACCEPTS = {
  video: ['video'], image: ['image'], v1: ['video', 'image'], v2: ['video', 'image'], audio: ['audio'],
};
const KIND_TAB = { video: 'Videos', image: 'Imágenes', audio: 'Audios' };
const ACCEPT_ATTR = { video: 'video/*', image: 'image/*', audio: 'audio/*' };
const KEYS = { video: 'videoClips', image: 'imageClips', v1: 'overlayV1Clips', v2: 'overlayV2Clips', audio: 'audioClips' };

export const acceptedKinds = (trackKind) => ACCEPTS[trackKind] || ['video'];

/** Duración real de un medio (null si no se puede leer). */
export function probeDuration(src, kind) {
  return new Promise((resolve) => {
    if (kind === 'image' || typeof document === 'undefined') { resolve(null); return; }
    const el = document.createElement(kind === 'audio' ? 'audio' : 'video');
    const timer = setTimeout(() => resolve(null), 8000);
    el.preload = 'metadata';
    el.muted = true;
    el.onloadedmetadata = () => { clearTimeout(timer); resolve(Number.isFinite(el.duration) ? el.duration : null); };
    el.onerror = () => { clearTimeout(timer); resolve(null); };
    el.src = src;
  });
}

export default function ReplaceClipModal({ target, onClose }) {
  const clip = useTimelineStore((s) => (s[KEYS[target.trackKind]] || []).find((c) => c.id === target.id));
  const kinds = acceptedKinds(target.trackKind);
  const [tab, setTab] = useState('gallery');
  const [kind, setKind] = useState(kinds[0]);
  const [assets, setAssets] = useState(null);
  const [picked, setPicked] = useState(null);       // { source_url, kind, label, thumb_url, asset_id, original_duration }
  const [keep, setKeep] = useState(true);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null);
  const [drag, setDrag] = useState(false);
  const fileRef = useRef(null);
  const limits = useUploadLimits();

  useEffect(() => {
    let alive = true;
    setAssets(null);
    galleryService.listAssets({ assetType: kind, limit: 60 })
      .then((d) => { if (alive) setAssets(d.assets || []); })
      .catch(() => { if (alive) setAssets([]); });
    return () => { alive = false; };
  }, [kind]);

  useEffect(() => {
    const esc = (e) => { if (e.key === 'Escape' && !busy) onClose(); };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [busy, onClose]);

  const pickAsset = async (a) => {
    const k = a.media_kind || a.asset_type;
    const base = {
      source_url: a.url, kind: k, label: a.filename || 'Clip', asset_id: a.id || null,
      thumb_url: a.thumbnail_url || a.metadata?.thumbnail_url || (k === 'image' ? a.url : null),
      original_duration: a.duration || a.metadata?.duration || null,
    };
    setPicked(base);
    if (!base.original_duration && k !== 'image') {
      const d = await probeDuration(a.url, k);
      setPicked((p) => (p && p.source_url === a.url ? { ...p, original_duration: d } : p));
    }
  };

  const uploadFile = async (file) => {
    if (!file) return;
    const k = kindOf(file);
    if (!kinds.includes(k)) {
      toast.error(`Esta pista acepta ${kinds.map((x) => KIND_TAB[x].toLowerCase()).join(' o ')}.`);
      return;
    }
    setBusy(true);
    setProgress(0);
    try {
      const localUrl = URL.createObjectURL(file);
      const duration = await probeDuration(localUrl, k);
      URL.revokeObjectURL(localUrl);
      const up = await uploadToR2(file, { onProgress: setProgress });
      setPicked({
        source_url: up.url, kind: k, label: file.name, asset_id: up.assetId,
        thumb_url: k === 'image' ? up.url : null, original_duration: duration,
      });
      toast.success(`${file.name} subido`);
    } catch (err) {
      toast.error(describeUploadError(err), { duration: 8000 });
    } finally {
      setBusy(false);
      setProgress(null);
    }
  };

  const note = useMemo(() => {
    if (!picked || !clip) return '';
    const d = picked.original_duration;
    if (picked.kind === 'image' || !d) return `Ocupa los mismos ${fmtSecs(clip.duration)}.`;
    if (keep) {
      return d > clip.duration
        ? `Dura ${fmtSecs(d)}: se usan sus primeros ${fmtSecs(clip.duration)}.`
        : `Dura ${fmtSecs(d)}: el clip queda de ${fmtSecs(d)}${useTimelineStore.getState().magnet ? ' y el Imán cierra el espacio' : ''}.`;
    }
    const diff = d - clip.duration;
    return Math.abs(diff) < 0.05 ? 'Dura lo mismo.' : `Lo que sigue se corre ${fmtSecs(Math.abs(diff))} hacia la ${diff > 0 ? 'derecha' : 'izquierda'}.`;
  }, [picked, clip, keep]);

  if (!clip) return null;

  const confirm = () => {
    if (!picked) return;
    const ok = useTimelineStore.getState().replaceClip(target.trackKind, target.id, picked, { keepDuration: keep });
    if (!ok) { toast.error('Esta pista no acepta ese tipo de contenido.'); return; }
    toast(`«${clip.label}» reemplazado`, {
      position: 'bottom-center',
      action: { label: 'Deshacer', onClick: () => useTimelineStore.getState().undoLast() },
    });
    onClose();
  };

  const maxLine = limits
    ? kinds.map((k) => `${limitMB(limits, k)} MB por ${k === 'video' ? 'video' : k === 'image' ? 'imagen' : 'audio'}`).join(' · ')
    : null;

  return (
    <div className="tl-gallery-overlay rcm-overlay" onClick={() => !busy && onClose()} data-testid="tl-replace-modal">
      <section className="rcm" role="dialog" aria-modal="true" aria-labelledby="rcm-title" onClick={(e) => e.stopPropagation()}>
        <header className="rcm-head">
          {clip.thumb_url
            ? <img src={clip.thumb_url} alt="" className="rcm-thumb" />
            : <span className="rcm-thumb rcm-thumb-empty" aria-hidden="true">{target.trackKind === 'audio' ? <Music size={18} /> : <Film size={18} />}</span>}
          <div className="rcm-head-txt">
            <span className="rcm-kicker">Reemplazar clip</span>
            <h2 id="rcm-title">{clip.label || 'Clip'}</h2>
            <span>{fmtTime(clip.start_time)} – {fmtTime(clip.start_time + clip.duration)} · {fmtSecs(clip.duration)} · se conservan la posición, los fundidos, el volumen y las transiciones</span>
          </div>
          <button type="button" className="rcm-x" onClick={onClose} disabled={busy} aria-label="Cerrar"><X size={18} /></button>
        </header>

        <div className="rcm-tabs">
          <div role="tablist" aria-label="Origen" className="rcm-seg">
            <button type="button" role="tab" aria-selected={tab === 'gallery'} className={tab === 'gallery' ? 'active' : ''} onClick={() => setTab('gallery')} data-testid="rcm-tab-gallery">Galería</button>
            <button type="button" role="tab" aria-selected={tab === 'upload'} className={tab === 'upload' ? 'active' : ''} onClick={() => setTab('upload')} data-testid="rcm-tab-upload">Subir desde la computadora</button>
          </div>
          {tab === 'gallery' && kinds.length > 1 && (
            <div className="rcm-kinds" role="group" aria-label="Tipo">
              {kinds.map((k) => (
                <button key={k} type="button" aria-pressed={kind === k} className={kind === k ? 'active' : ''} onClick={() => setKind(k)}>{KIND_TAB[k]}</button>
              ))}
            </div>
          )}
        </div>

        {tab === 'gallery' ? (
          <div className="rcm-grid" data-testid="rcm-grid">
            {assets === null && <div className="rcm-empty"><Loader2 size={18} className="spin" /></div>}
            {assets && assets.length === 0 && <div className="rcm-empty">No hay {KIND_TAB[kind].toLowerCase()} en la galería. Subí uno desde la computadora.</div>}
            {assets && assets.map((a) => {
              const k = a.media_kind || a.asset_type;
              const on = picked?.source_url === a.url;
              const thumb = k === 'image' ? a.url : (a.thumbnail_url || a.metadata?.thumbnail_url);
              return (
                <button key={a.id} type="button" className={`rcm-card ${on ? 'is-on' : ''}`} aria-pressed={on} onClick={() => pickAsset(a)} data-testid={`rcm-card-${a.id}`}>
                  <span className="rcm-card-media">
                    {thumb ? <img src={thumb} alt="" loading="lazy" /> : (k === 'audio' ? <Music size={26} /> : <Film size={26} />)}
                  </span>
                  <span className="rcm-card-name">{a.filename}</span>
                </button>
              );
            })}
          </div>
        ) : (
          <div className="rcm-upload">
            <div
              className={`rcm-drop ${drag ? 'is-drag' : ''}`}
              onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
              onDragLeave={() => setDrag(false)}
              onDrop={(e) => { e.preventDefault(); setDrag(false); uploadFile(e.dataTransfer.files?.[0]); }}
            >
              <Upload size={34} aria-hidden="true" />
              <b>Arrastrá el archivo acá</b>
              <button type="button" onClick={() => fileRef.current?.click()} disabled={busy} data-testid="rcm-choose">Elegir archivo…</button>
              <input ref={fileRef} type="file" hidden accept={kinds.map((k) => ACCEPT_ATTR[k]).join(',')}
                onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; uploadFile(f); }} data-testid="rcm-file" />
              {busy && (
                <span className="rcm-progress" role="status">
                  <span><i style={{ width: `${progress || 0}%` }} /></span>
                  Subiendo… {progress != null ? `${progress} %` : ''}
                </span>
              )}
              {!busy && picked && tab === 'upload' && <span className="rcm-ready">Listo: {picked.label}</span>}
            </div>
            {maxLine && (
              <p className="rcm-limit" role="note"><Info size={15} aria-hidden="true" /> Tu plan <b>{limits.plan_label}</b> permite hasta {maxLine}. Si el archivo pesa más, te avisamos antes de subirlo.</p>
            )}
          </div>
        )}

        <fieldset className="rcm-dur">
          <legend>Duración</legend>
          <label><input type="radio" name="rcm-dur" checked={keep} onChange={() => setKeep(true)} /> Mantener {fmtSecs(clip.duration)} (el timeline no se mueve)</label>
          <label><input type="radio" name="rcm-dur" checked={!keep} onChange={() => setKeep(false)} disabled={picked?.kind === 'image'} /> Usar la duración del nuevo{picked?.original_duration ? ` (${fmtSecs(picked.original_duration)})` : ''}</label>
        </fieldset>

        <footer className="rcm-foot">
          <span>{note}</span>
          <span className="rcm-actions">
            <button type="button" className="rcm-ghost" onClick={onClose} disabled={busy}>Cancelar</button>
            <button type="button" className="rcm-primary" onClick={confirm} disabled={!picked || busy} data-testid="rcm-confirm">
              <ArrowRightLeft size={15} aria-hidden="true" /> Reemplazar
            </button>
          </span>
        </footer>
      </section>
    </div>
  );
}
